// @ts-strict-ignore
import React, { useLayoutEffect, useRef, useState } from 'react';

import { css } from 'glamor';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { amountToCurrency } from 'loot-core/shared/util';

import { type CSSProperties, theme } from '../../../style';
import { getRandomChartColor } from '../../budget-template/util';
import { AlignedText } from '../../common/AlignedText';
import { View } from '../../common/View';

type PayloadItem = {
  name: string;
  value: number;
  color: string;
  payload: {
    name: string;
    color: number | string;
  };
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: PayloadItem[];
  label?: string;
  isOverviewMode?: boolean;
  baseGraphTitle?: string;
  compareToGraphTitle?: string;
};

const CustomLegend = props => {
  return (
    <div
      className={`${css({
        zIndex: 1000,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        paddingLeft: '150px',
      })}`}
    >
      <div
        className={`${css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        })}`}
      >
        <img width={16} src="/images/palette.svg" />
        <span>{props.payload[0].value}</span>
      </div>
      <div
        className={`${css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        })}`}
      >
        <div
          className={`${css({
            width: '16px',
            height: '16px',
            backgroundColor: '#E9EBED',
          })}`}
        />
        <span>{props.payload[1].value}</span>
      </div>
    </div>
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  isOverviewMode,
  baseGraphTitle,
  compareToGraphTitle,
}: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={`${css({
          zIndex: 1000,
          pointerEvents: 'none',
          borderRadius: 2,
          boxShadow: '0 1px 6px rgba(0, 0, 0, .20)',
          backgroundColor: theme.menuBackground,
          color: theme.menuItemText,
          padding: 10,
        })}`}
      >
        <div>
          <div style={{ marginBottom: 10 }}>
            <strong>{label}</strong>
          </div>
          <div style={{ lineHeight: 1.4 }}>
            <AlignedText
              left={isOverviewMode ? 'Actual amount' : baseGraphTitle}
              right={amountToCurrency(payload[0].payload.baseAmount)}
              style={{ color: payload[0].color }}
            />
            <AlignedText
              left={isOverviewMode ? 'Budgeted amount' : compareToGraphTitle}
              right={amountToCurrency(payload[0].payload.compareToAmount)}
              style={{ color: payload[0].color }}
            />
          </div>
        </div>
      </div>
    );
  }
};

type StackedBarGraphProps = {
  style?: CSSProperties;
  data: {
    name: string;
    baseAmount: number;
    compareToAmount: number;
  }[];
  compact?: boolean;
  isOverviewMode?: boolean;
  baseGraphTitle?: string;
  compareToGraphTitle?: string;
  onNavigate?: (category: string) => void;
};

const legendPayloadForOverview = [
  {
    name: 'Budgeted amount',
    key: 'baseAmount',
    color: '#E9EBED',
  },
];

const legendPayload = (baseTitle, compareToTitle) => {
  return [
    {
      name: baseTitle,
      key: 'baseAmount',
      color: '',
    },
    {
      name: compareToTitle,
      key: 'compareToAmount',
      color: '',
    },
  ];
};

export function SpendingByCategoryGraph({
  style,
  data,
  compact,
  isOverviewMode,
  baseGraphTitle = '',
  compareToGraphTitle = '',
  onNavigate,
}: StackedBarGraphProps) {
  const [height, setHeight] = useState<number>(150);
  const barClickRef = useRef<boolean>(false);

  useLayoutEffect(() => {
    const height = data?.length
      ? data?.length * (16 + 6) + (data?.length - 1) * 16
      : 50;
    setHeight(Math.max(height, 159));
  }, [data?.length]);

  const handleChartClick = (event) => {
    if (event && event.activeLabel) {
      barClickRef.current = true;
      onNavigate?.(event.activeLabel);
    }
  };

  const handleChartWrapperClick = (event) => {
    if (barClickRef.current) {
      event.stopPropagation();
      event.preventDefault();
      barClickRef.current = false;
    }
  };

  return (
    <View style={{ padding: 20 }} onClick={handleChartWrapperClick}>
      <ResponsiveContainer width="100%" height={height}>
        {data?.length ? (
          <BarChart
            data={data}
            layout="vertical"
            barCategoryGap={16}
            barSize={8}
            barGap={6}
            onClick={handleChartClick}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={tick => amountToCurrency(tick)}
            />
            <YAxis dataKey="name" type="category" width={150} />
            <ReferenceLine x={0} stroke="#000" />
            <Tooltip
              content={
                <CustomTooltip
                  isOverviewMode={isOverviewMode}
                  baseGraphTitle={baseGraphTitle}
                  compareToGraphTitle={compareToGraphTitle}
                />
              }
            />
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              {...(!isOverviewMode
                ? { content: props => CustomLegend(props) }
                : {})}
              formatter={value => (
                <span style={{ color: theme.pageText }}>{value}</span>
              )}
              payload={(isOverviewMode
                ? legendPayloadForOverview
                : legendPayload(baseGraphTitle, compareToGraphTitle)
              ).map(p => ({
                value: p.name,
                type: 'rect',
                color: p.color,
              }))}
            />
            <Bar dataKey="baseAmount" fill={theme.buttonPrimaryBackground}>
              {data.map((d, idx) => (
                <Cell key={d.baseAmount} fill={getRandomChartColor(d.name)} />
              ))}
            </Bar>
            <Bar dataKey="compareToAmount" fill="#E9EBED">
              {data.map((d, idx) => (
                <Cell key={d.compareToAmount} fill="#E9EBED" />
              ))}
            </Bar>
          </BarChart>
        ) : (
          <View
            style={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            No data to show at this moment
          </View>
        )}
      </ResponsiveContainer>
    </View>
  );
}
